import { useState } from 'react';
import { C, MONO, mono } from '../theme';
import { Icon } from '../icons';
import { Loading, Empty } from '../components/States';
import { useToast } from '../components/Toast';
import { api, ApiError } from '../api';
import type { MyTenancy } from '../api';
import { useAsync } from '../useAsync';
import { inr, fmtPeriod, fmtDate, fmtPeriodShort } from '../format';
import { Row } from './Row';
import { InstallCard } from '../components/InstallCard';

/** Tenant home (GET /my/tenancy). The current month's net-of-TDS figure + quick actions. */
export function TenantHome({ go }: { go: (tab: string) => void }) {
  const notify = useToast();
  const [paid, setPaid] = useState(false);
  const { data, loading, error, reload } = useAsync<MyTenancy>(() => api.myTenancy(), []);

  if (loading) return <Loading label="Loading your tenancy…" minHeight={300} />;
  if (error || !data) {
    return (
      <Empty icon="home" title="Couldn't load your tenancy" hint="Please try again in a moment." minHeight={300}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  }

  const dueLabel =
    data.dueInDays == null
      ? `Due ${fmtDate(data.dueDate)}`
      : data.dueInDays < 0
        ? `Due ${data.dueDate ? fmtDate(data.dueDate) : ''} · ${Math.abs(data.dueInDays)} days overdue`
        : data.dueInDays === 0
          ? 'Due today'
          : `Due in ${data.dueInDays} days`;

  async function markPaid() {
    try {
      await api.markPaid(data!.period);
      setPaid(true);
      notify('Marked as paid. Owner notified.');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not update. Try again.');
    }
  }

  return (
    <>
      <div style={{ fontSize: 12.5, color: C.muted }}>
        {data.tenantName}
        <b style={{ color: C.ink, fontSize: 15, display: 'block', fontWeight: 650, marginTop: 1 }}>
          {data.unitLabel} · {data.propertyName}
        </b>
      </div>

      {/* rent card */}
      <div style={{ background: `linear-gradient(160deg, ${C.accent}, ${C.accent2})`, borderRadius: 16, padding: '17px 18px', color: '#fff' }}>
        <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,.75)', fontWeight: 600 }}>Rent for {fmtPeriod(data.period)}</div>
        <div style={{ ...mono(), fontSize: 33, fontWeight: 700, letterSpacing: '-.02em', margin: '5px 0 2px' }}>{inr(data.netExpected)}</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.8)', fontFamily: MONO }}>
          {data.tdsAmount > 0
            ? `${inr(data.rent)} rent − ${inr(data.tdsAmount)} TDS you deduct`
            : `${inr(data.rent)} rent`}
        </div>
        <div style={{ display: 'inline-flex', marginTop: 11, background: 'rgba(255,255,255,.16)', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999 }}>{dueLabel}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={markPaid} disabled={paid || data.status === 'paid'} style={{ flex: 1, border: 0, borderRadius: 10, padding: 10, fontWeight: 650, fontSize: 13, background: '#fff', color: C.accent2, cursor: paid ? 'default' : 'pointer', opacity: paid || data.status === 'paid' ? 0.7 : 1 }}>
            {paid || data.status === 'paid' ? 'Marked paid' : "I've paid"}
          </button>
          <button onClick={() => go('docs')} style={{ flex: 1, border: 0, borderRadius: 10, padding: 10, fontWeight: 650, fontSize: 13, background: 'rgba(255,255,255,.15)', color: '#fff', cursor: 'pointer' }}>
            View invoice
          </button>
        </div>
      </div>

      {/* quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 9 }}>
        {([
          ['Statement', 'ledger', 'statement'],
          ['Documents', 'doc', 'docs'],
          ['Request', 'wrench', 'requests'],
        ] as const).map(([labelText, icon, dest]) => (
          <button key={labelText} onClick={() => go(dest)} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, padding: '13px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, color: C.ink, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <span style={{ width: 34, height: 34, borderRadius: 10, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon n={icon} size={18} />
            </span>
            {labelText}
          </button>
        ))}
      </div>

      {/* coming up */}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px 8px' }}>
          <h4 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 650 }}>Coming up</h4>
        </div>
        {data.next ? (
          <Row icon="cal" tone="br" t={`Rent for ${fmtPeriodShort(data.next.period)}`} s={`Due ${fmtDate(data.next.dueDate)}`} r={inr(data.next.netExpected)} />
        ) : (
          <Row icon="cal" tone="br" t="Next rent" s="Nothing scheduled yet" chev />
        )}
      </div>

      {/* Install offer, last: a tenant is the pure mobile user here. Renders
          nothing when already installed or when the browser has no offer. */}
      <InstallCard />
    </>
  );
}

const retryBtn: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
